/**
 * dp-evaluator.js — Dynamic Product evaluator (browser build, IIFE)
 *
 * Replica fiel del motor de PrestaShop DP module. Mismo código y misma lógica
 * que shopify-configurador-app/web/api/dp-evaluator.mjs (backend en Node).
 * IMPORTANT: keep both files in sync.
 *
 * Registra en window.DPEvaluator:
 *   - evaluateAccessory({ equation, intervals, basePrices, state })
 *       → { total, breakdown }
 *   - evaluateCondition(whenStr, state, basePrices?, intervals?)
 *       → boolean
 *   - applyVisibility(rules, state, basePrices?, intervals?)
 *       → { hiddenFields:Set<string>, hiddenOptions:{[fieldName]:Set<string>} }
 *
 * Range convention:
 *   { type:'range', min, max }  →  state[field] >= min AND state[field] < max
 *   max === 0  →  no upper bound (state[field] >= min only)
 *
 * Cargar ANTES de configurator.js en el liquid:
 *   <script src="{{ 'dp-evaluator.js' | asset_url }}" defer></script>
 *   <script src="{{ 'configurator.js'   | asset_url }}" defer></script>
 */
(function () {
  'use strict';

  var COMPARE_OPS = { '<': 1, '<=': 1, '>': 1, '>=': 1, '=': 1, '==': 1, '!=': 1 };

  // ---- Tokenizer ---------------------------------------------------------
  function tokenize(src) {
    var tokens = [];
    var i = 0;
    while (i < src.length) {
      var c = src[i];
      if (/\s/.test(c)) { i++; continue; }
      if (c === '[') {
        var end = src.indexOf(']', i);
        if (end < 0) throw new Error('Unclosed [ at position ' + i);
        tokens.push({ type: 'var', value: src.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
      if (/[0-9.]/.test(c)) {
        var j = i;
        while (j < src.length && /[0-9.]/.test(src[j])) j++;
        tokens.push({ type: 'num', value: parseFloat(src.slice(i, j)) });
        i = j;
        continue;
      }
      if ('+-*/()'.indexOf(c) >= 0) {
        tokens.push({ type: 'op', value: c });
        i++;
        continue;
      }
      if (c === '|') {
        if (i + 1 < src.length && src[i + 1] === '|') i++;
        tokens.push({ type: 'op', value: '|' });
        i++;
        continue;
      }
      if (c === '&') {
        if (i + 1 < src.length && src[i + 1] === '&') i++;
        tokens.push({ type: 'op', value: '&' });
        i++;
        continue;
      }
      if (c === '<' || c === '>' || c === '=' || c === '!') {
        var op = c;
        if (i + 1 < src.length && src[i + 1] === '=') { op += '='; i++; }
        tokens.push({ type: 'op', value: op });
        i++;
        continue;
      }
      throw new Error('Unexpected character "' + c + '" at position ' + i + ' in: ' + src);
    }
    return tokens;
  }

  // ---- Parser (recursive descent) ----------------------------------------
  // expr := or, or := and ('|' and)*, and := compare ('&' compare)*,
  // compare := addsub (cmpop addsub)?
  function parse(tokens) {
    var pos = 0;
    function peek() { return tokens[pos]; }
    function consume() { return tokens[pos++]; }

    function expr() {
      var left = andExpr();
      while (peek() && peek().type === 'op' && peek().value === '|') {
        consume();
        var right = andExpr();
        left = { type: 'or', left: left, right: right };
      }
      return left;
    }
    function andExpr() {
      var left = compareExpr();
      while (peek() && peek().type === 'op' && peek().value === '&') {
        consume();
        var right = compareExpr();
        left = { type: 'and', left: left, right: right };
      }
      return left;
    }
    function compareExpr() {
      var left = addsub();
      var t = peek();
      if (t && t.type === 'op' && COMPARE_OPS[t.value]) {
        var op = consume().value;
        var right = addsub();
        return { type: 'cmp', op: op, left: left, right: right };
      }
      return left;
    }
    function addsub() {
      var left = muldiv();
      while (peek() && peek().type === 'op' && (peek().value === '+' || peek().value === '-')) {
        var op = consume().value;
        var right = muldiv();
        left = { type: 'bin', op: op, left: left, right: right };
      }
      return left;
    }
    function muldiv() {
      var left = unary();
      while (peek() && peek().type === 'op' && (peek().value === '*' || peek().value === '/')) {
        var op = consume().value;
        var right = unary();
        left = { type: 'bin', op: op, left: left, right: right };
      }
      return left;
    }
    function unary() {
      if (peek() && peek().type === 'op' && peek().value === '-') {
        consume();
        return { type: 'neg', expr: primary() };
      }
      return primary();
    }
    function primary() {
      var t = consume();
      if (!t) throw new Error('Unexpected end of expression');
      if (t.type === 'num') return { type: 'num', value: t.value };
      if (t.type === 'var') return { type: 'var', name: t.value };
      if (t.type === 'op' && t.value === '(') {
        var e = expr();
        var close = consume();
        if (!close || close.value !== ')') throw new Error('Expected )');
        return e;
      }
      throw new Error('Unexpected token ' + JSON.stringify(t));
    }
    return expr();
  }

  // ---- Variable resolver -------------------------------------------------
  function makeResolver(opts) {
    var basePrices = opts.basePrices;
    var intervals  = opts.intervals;
    var state      = opts.state;
    var stack      = opts._stack || {};

    return function resolve(name) {
      if (stack[name]) return 0; // circular guard

      // 1) Computed via intervals (first match wins)
      if (intervals && intervals.length) {
        for (var k = 0; k < intervals.length; k++) {
          var iv = intervals[k];
          if (iv.target !== name) continue;
          if (intervalMatches(iv.when, state)) {
            stack[name] = true;
            var inner = makeResolver({ basePrices: basePrices, intervals: intervals, state: state, _stack: stack });
            var result = evalAst(parse(tokenize(iv.formula)), inner);
            stack[name] = false;
            return result;
          }
        }
      }
      // 2) Selection or size from current state
      if (state && Object.prototype.hasOwnProperty.call(state, name)
          && state[name] !== undefined && state[name] !== null && state[name] !== '') {
        var num = parseFloat(state[name]);
        if (!isNaN(num)) return num;
      }
      // 3) Constant base price
      if (basePrices && Object.prototype.hasOwnProperty.call(basePrices, name)) {
        return Number(basePrices[name]) || 0;
      }
      // 4) Default
      return 0;
    };
  }

  function intervalMatches(conditions, state) {
    if (!conditions || !conditions.length) return false;
    for (var i = 0; i < conditions.length; i++) {
      var c = conditions[i];
      var raw = state ? state[c.field] : undefined;
      var v = parseFloat(raw);
      if (isNaN(v)) return false;
      if (c.type === 'range') {
        if (v < Number(c.min)) return false;
        if (Number(c.max) !== 0 && v >= Number(c.max)) return false;
      } else if (c.type === 'values') {
        var wanted = (c.in || []).map(Number);
        if (wanted.indexOf(v) < 0) return false;
      } else {
        return false;
      }
    }
    return true;
  }

  // ---- AST evaluator -----------------------------------------------------
  function evalAst(node, resolve) {
    switch (node.type) {
      case 'num': return node.value;
      case 'var': return resolve(node.name);
      case 'neg': return -evalAst(node.expr, resolve);
      case 'or':
        return evalAst(node.left, resolve) ? 1 : (evalAst(node.right, resolve) ? 1 : 0);
      case 'and':
        return evalAst(node.left, resolve) ? (evalAst(node.right, resolve) ? 1 : 0) : 0;
      case 'bin':
        var l = evalAst(node.left, resolve);
        var r = evalAst(node.right, resolve);
        switch (node.op) {
          case '+': return l + r;
          case '-': return l - r;
          case '*': return l * r;
          case '/': return r === 0 ? 0 : l / r;
        }
        throw new Error('Unknown op ' + node.op);
      case 'cmp':
        var lc = evalAst(node.left, resolve);
        var rc = evalAst(node.right, resolve);
        switch (node.op) {
          case '<':  return lc <  rc ? 1 : 0;
          case '<=': return lc <= rc ? 1 : 0;
          case '>':  return lc >  rc ? 1 : 0;
          case '>=': return lc >= rc ? 1 : 0;
          case '=':
          case '==': return lc === rc ? 1 : 0;
          case '!=': return lc !== rc ? 1 : 0;
        }
        throw new Error('Unknown cmp op ' + node.op);
    }
    throw new Error('Unknown AST node ' + node.type);
  }

  // ---- Public helpers ----------------------------------------------------
  var SPECIAL_VARS = { 'total': 1, 'transporte': 1 };

  function flattenAddSub(node) {
    var out = [];
    function walk(n, sign) {
      if (n.type === 'bin' && (n.op === '+' || n.op === '-')) {
        walk(n.left, sign);
        walk(n.right, n.op === '+' ? sign : -sign);
        return;
      }
      out.push({ sign: sign, node: n });
    }
    walk(node, 1);
    return out;
  }

  function describeTerm(node) {
    if (node.type === 'var') return '[' + node.name + ']';
    if (node.type === 'num') return String(node.value);
    if (node.type === 'neg') return '-' + describeTerm(node.expr);
    return '<expr>';
  }

  function round2(n) { return Math.round(n * 100) / 100; }

  function evaluateAccessory(opts) {
    if (!opts || !opts.equation) return { total: 0, breakdown: {} };
    var tokens = tokenize(opts.equation);
    var ast = parse(tokens);
    var resolve = makeResolver({
      basePrices: opts.basePrices, intervals: opts.intervals, state: opts.state
    });
    var terms;
    try { terms = flattenAddSub(ast); } catch (e) { terms = null; }
    if (!terms) {
      return { total: evalAst(ast, resolve), breakdown: { _raw: 'non-flat' } };
    }
    var breakdown = {};
    var sum = 0;
    for (var i = 0; i < terms.length; i++) {
      var t = terms[i];
      if (t.node.type === 'var' && SPECIAL_VARS[t.node.name]) continue;
      var v = t.sign * evalAst(t.node, resolve);
      sum += v;
      breakdown[describeTerm(t.node)] = round2(v);
    }
    return { total: round2(sum), breakdown: breakdown };
  }

  function evaluateCondition(whenStr, state, basePrices, intervals) {
    if (!whenStr) return false;
    var tokens = tokenize(whenStr);
    var ast = parse(tokens);
    var resolve = makeResolver({
      basePrices: basePrices || {}, intervals: intervals || [], state: state
    });
    return evalAst(ast, resolve) !== 0;
  }

  function applyVisibility(rules, state, basePrices, intervals) {
    var hiddenFields = new Set();
    var hiddenOptions = {};
    // lockedFields: { campo: { mirror: 'otro_campo', note: '...' } }. El campo
    // queda bloqueado y copia la opcion elegida en "mirror" (regla lock_fields).
    var lockedFields = {};
    if (!rules) return { hiddenFields: hiddenFields, hiddenOptions: hiddenOptions, lockedFields: lockedFields };
    for (var i = 0; i < rules.length; i++) {
      var rule = rules[i];
      var matches = false;
      try {
        matches = evaluateCondition(rule.when, state, basePrices, intervals);
      } catch (e) {
        if (typeof console !== 'undefined' && console.warn) {
          console.warn('[dp-evaluator] skipped rule, parse error:', rule.when, '—', e.message);
        }
        continue;
      }
      if (!matches) continue;
      (rule.hide_fields || []).forEach(function (f) { hiddenFields.add(f); });
      var opts = rule.hide_options || [];
      for (var j = 0; j < opts.length; j++) {
        var o = opts[j];
        if (!hiddenOptions[o.field]) hiddenOptions[o.field] = new Set();
        hiddenOptions[o.field].add(String(o.value));
      }
      var locks = rule.lock_fields || [];
      for (var k = 0; k < locks.length; k++) {
        var l = locks[k];
        if (!l || !l.field || !l.mirror) continue;
        lockedFields[l.field] = { mirror: String(l.mirror), note: typeof l.note === 'string' ? l.note : '' };
      }
    }
    return { hiddenFields: hiddenFields, hiddenOptions: hiddenOptions, lockedFields: lockedFields };
  }

  // Register on window
  window.DPEvaluator = {
    evaluateAccessory: evaluateAccessory,
    evaluateCondition: evaluateCondition,
    applyVisibility:   applyVisibility,
    _internal: {
      tokenize: tokenize, parse: parse, evalAst: evalAst,
      makeResolver: makeResolver, intervalMatches: intervalMatches
    }
  };
})();
